// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { BadgeModel } from "@insite/client-framework/Types/ApiModels";
import FlexItem, { FlexItemProps } from "@insite/content-library/Widgets/Brand/FlexItem";
import FlexWrapContainer, { FlexWrapContainerProps } from "@insite/content-library/Widgets/Brand/FlexWrapContainer";
import Badge from "@insite/mobius/Badge";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Skeleton from "@synd/App/Components/Skeleton/Skeleton";
import React, { useState } from "react";
import { css } from "styled-components";

interface Props {
    badgeList: BadgeModel[];
    maxBadges?: number;
    extendedStyles?: ProductImageBadgesStyles;
    numberOfColumns?: number;
    // XNG-Changes
    hiddenVisibility?: boolean;
}

export interface ProductImageBadgesStyles {
    container?: InjectableCss;
    imageContainer?: FlexWrapContainerProps;
    imageItem?: FlexItemProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    image?: LazyImageProps;
}

export const productImageBadgesStyles: ProductImageBadgesStyles = {
    imageContainer: {
        css: css`
            justify-content: left;
        `,
    },
    badgeImageProps: {
        style: {
            width: "100%",
            height: "auto",
        },
    },
    imageItem: {
        css: css`
            padding: 5px;
            justify-content: center;
        `,
    },
};

const ProductImageBadges = ({
    badgeList,
    maxBadges,
    extendedStyles,
    numberOfColumns,
    // XNG-Changes
    hiddenVisibility,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productImageBadgesStyles, extendedStyles));

    // <XNG-Changes>
    if (badgeList.length === 0 && !hiddenVisibility) {
        return null;
    }
    if (hiddenVisibility && badgeList.length === 0) {
        return <Skeleton disabledAnimation height="15px" width="100%" />;
    }
    // </XNG-Changes>

    return (
        <StyledWrapper {...styles.container} data-test-selector="productImageBadges">
            <FlexWrapContainer {...styles.imageContainer}>
                {/* XNG-Changes: to show all the badges if nat passing maxBadges */}
                {(maxBadges ? badgeList.slice(0, maxBadges) : badgeList).map(badge => (
                    <FlexItem key={badge.id} flexColumns={numberOfColumns ?? 4} {...styles.imageItem}>
                        <Badge
                            type={badge.badgeType}
                            value={badge.largeImageBadgePath}
                            imageAltText={badge.imageAltText ? badge.imageAltText : badge.name}
                            imageBadgeProps={styles.badgeImageProps}
                            // XNG-Changes
                            {...{ name: badge.name }}
                        />
                    </FlexItem>
                ))}
            </FlexWrapContainer>
        </StyledWrapper>
    );
};

export default ProductImageBadges;
